import { useMemo, useState } from 'react';
import { Button, Offcanvas } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import type { DirTaskCategoryType, TaskTypeDirType } from '@econtractor/types';
import { useTheme } from 'context/ThemeContext';
import { useSettings } from 'context/SettingsContext';
import { selectStylesDark, selectStylesLigth } from 'utils/helpers/structs/selectStyles';
import { tDirName } from 'utils/helpers/i18n/tDirName';
import SolarIcon from 'components/icons/SolarIcon';
import EToolTip from 'components/others/EToolTip';
import TaskTypeChip from '../shared/TaskTypeChip';
import type { TaskTypeOption } from '../shared/TaskTypeSelect';
import type { TaskCategoryOption } from './types';

interface TaskCategoryFilterProps {
    filterCategoryId: number | null;
    setFilterCategoryId: (id: number | null) => void;
    filterTypeId: number | null;
    setFilterTypeId: (id: number | null) => void;
}

export const TaskCategoryFilterSelect = ({
    filterCategoryId,
    setFilterCategoryId,
}: Pick<TaskCategoryFilterProps, 'filterCategoryId' | 'setFilterCategoryId'>) => {
    const { t } = useTranslation(['Tasks', 'common']);
    const { darkTheme } = useTheme();
    const { localSettings } = useSettings();
    const selectTheme = darkTheme ? selectStylesDark : selectStylesLigth;

    const categoryOptions = useMemo<TaskCategoryOption[]>(
        () =>
            ((localSettings?.DIRS?.TaskCategory || []) as DirTaskCategoryType[])
                .filter((item) => !item.hidden || item.ID === filterCategoryId)
                .map((item) => ({ value: item.ID, label: item.categoryName })),
        [localSettings?.DIRS, filterCategoryId],
    );

    return (
        <>
            <label className="form-label text-muted fs-12 mb-1">{t('filterCategory')}</label>
            <Select<TaskCategoryOption, false>
                isClearable
                options={categoryOptions}
                value={categoryOptions.find((opt) => opt.value === filterCategoryId) || null}
                onChange={(opt) => setFilterCategoryId(opt?.value ? Number(opt.value) : null)}
                placeholder={t('filterCategory')}
                className="w-100"
                styles={{
                    ...selectTheme,
                    menuPortal: (base) => ({ ...base, zIndex: 2100 }),
                }}
                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                menuPosition="fixed"
            />
        </>
    );
};

const TaskTypeFilterSelect = ({
    filterTypeId,
    setFilterTypeId,
}: Pick<TaskCategoryFilterProps, 'filterTypeId' | 'setFilterTypeId'>) => {
    const { t } = useTranslation(['Tasks']);
    const { darkTheme } = useTheme();
    const { localSettings } = useSettings();
    const selectTheme = darkTheme ? selectStylesDark : selectStylesLigth;

    const typeOptions = useMemo<TaskTypeOption[]>(
        () =>
            ((localSettings?.DIRS?.TaskType || []) as TaskTypeDirType[]).map((item) => ({
                value: item.ID,
                label: tDirName(t, item.i18nKey, item.taskTypeName),
                icon: item.taskTypeIcon || null,
                colors: item.taslTypeJson || null,
            })),
        [localSettings?.DIRS, t],
    );

    return (
        <>
            <label className="form-label text-muted fs-12 mb-1">{t('filterType')}</label>
            <Select<TaskTypeOption, false>
                isClearable
                options={typeOptions}
                value={typeOptions.find((opt) => opt.value === filterTypeId) || null}
                onChange={(opt) => setFilterTypeId(opt?.value ? Number(opt.value) : null)}
                placeholder={t('filterType')}
                className="w-100"
                styles={{
                    ...selectTheme,
                    menuPortal: (base) => ({ ...base, zIndex: 2100 }),
                }}
                formatOptionLabel={(option) => (
                    <TaskTypeChip name={option.label} icon={option.icon} colors={option.colors} />
                )}
                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                menuPosition="fixed"
            />
        </>
    );
};

const TaskCategoryFilter = ({
    filterCategoryId,
    setFilterCategoryId,
    filterTypeId,
    setFilterTypeId,
}: TaskCategoryFilterProps) => {
    const { t } = useTranslation(['Tasks', 'common']);
    const [show, setShow] = useState(false);
    const [draftCategoryId, setDraftCategoryId] = useState<number | null>(filterCategoryId);
    const [draftTypeId, setDraftTypeId] = useState<number | null>(filterTypeId);
    const filterActive = Boolean(filterCategoryId || filterTypeId);

    const handleHide = () => setShow(false);

    const handleOpen = () => {
        setDraftCategoryId(filterCategoryId);
        setDraftTypeId(filterTypeId);
        setShow(true);
    };

    return (
        <>
            <EToolTip title={t('toolFilter')}>
                <span className="d-inline-block">
                    <Button
                        className="fs-14 px-2"
                        size="sm"
                        variant={filterActive ? 'danger' : 'primary'}
                        onClick={(e) => {
                            e.currentTarget.blur();
                            handleOpen();
                        }}
                    >
                        <SolarIcon name="funnel" className="fs-14 p-0" />
                    </Button>
                </span>
            </EToolTip>

            <Offcanvas show={show} onHide={handleHide} placement="end">
                <Offcanvas.Header closeButton className="border-bottom">
                    <Offcanvas.Title>{t('toolFilter')}</Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body className="position-relative pb-0 d-flex flex-column px-0">
                    <div className="off-filter-form d-flex flex-column h-100">
                        <div className="off-filter-content px-3">
                            <div className="mb-3">
                                <TaskCategoryFilterSelect
                                    filterCategoryId={draftCategoryId}
                                    setFilterCategoryId={setDraftCategoryId}
                                />
                            </div>
                            <div className="mb-3">
                                <TaskTypeFilterSelect
                                    filterTypeId={draftTypeId}
                                    setFilterTypeId={setDraftTypeId}
                                />
                            </div>
                        </div>
                        <div className="p-3 offcanvas-footer off-filter-footer">
                            <div className="d-flex justify-content-end gap-2">
                                <Button
                                    variant="secondary"
                                    onClick={() => {
                                        setFilterCategoryId(null);
                                        setFilterTypeId(null);
                                        handleHide();
                                    }}
                                >
                                    {t('common:buttons.reset')}
                                </Button>
                                <Button
                                    variant="info"
                                    className="min100px"
                                    onClick={() => {
                                        setFilterCategoryId(draftCategoryId);
                                        setFilterTypeId(draftTypeId);
                                        handleHide();
                                    }}
                                >
                                    {t('common:buttons.apply')}
                                </Button>
                            </div>
                        </div>
                    </div>
                </Offcanvas.Body>
            </Offcanvas>
        </>
    );
};

export default TaskCategoryFilter;
